import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, articles, type Article } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setFilteredArticles(articles.filter((a) => a.categorySlug === activeSlug));
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-24 pb-10 bg-background-100/60">
          <div className="w-full px-6 md:px-10 max-w-6xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium whitespace-nowrap">{activeCategory?.name || '分类'}</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
              {activeCategory?.name}
            </h1>
            <p className="text-foreground-600 text-base max-w-2xl">
              {activeCategory?.description}
            </p>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 pb-6 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex gap-2 overflow-x-auto pb-4 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer no-underline ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-10 bg-background-50">
          <div className="max-w-6xl mx-auto">
            {filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group rounded-lg bg-background-50 border border-background-200/60 overflow-hidden hover:shadow-sm transition-all duration-300 no-underline"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        width={400}
                        height={225}
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs font-medium text-primary-500 bg-primary-100 px-2.5 py-1 rounded-full">
                          {article.category}
                        </span>
                        <span className="text-xs text-foreground-400">{article.readTime}</span>
                      </div>
                      <h3 className="font-heading text-base font-bold text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-background-200/60">
                        <span className="text-xs text-foreground-400">{article.author}</span>
                        <span className="text-xs text-foreground-300">·</span>
                        <span className="text-xs text-foreground-400">{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}